import React, { useState } from 'react';
import { Modal, Form, Input, Alert } from 'antd';
import { BASE_URL } from '../conf/config';

export const PASSWORD_RULES =
  'At least 12 characters. Not a common or leaked password, not close to your username, and not your current one.';

// The password endpoints work without a login (forgotten or expired passwords), so plain fetch.
export async function postPassword(path, body) {
  try {
    const response = await fetch(`${BASE_URL}/api/password/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (response.status === 429) {
      return { ok: false, errors: ['Too many attempts. Please wait a while and try again.'] };
    }
    const data = await response.json();
    return { ok: response.ok, detail: data.detail, errors: data.errors || [] };
  } catch (e) {
    return { ok: false, errors: ['The server could not be reached. Please try again.'] };
  }
}

// New password twice, with the rules shown and the server's objections listed.
export function NewPasswordFields() {
  return (
    <>
      <Form.Item name="new_password" label="New password" extra={PASSWORD_RULES}
        rules={[{ required: true, message: 'Please enter a new password' }]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item name="repeat" label="Repeat new password" dependencies={['new_password']}
        rules={[
          { required: true, message: 'Please repeat the new password' },
          ({ getFieldValue }) => ({
            validator: (_, value) => (!value || value === getFieldValue('new_password')
              ? Promise.resolve() : Promise.reject(new Error('The two passwords differ'))),
          }),
        ]}>
        <Input.Password autoComplete="new-password" />
      </Form.Item>
    </>
  );
}

export function ErrorList({ errors }) {
  if (!errors.length) return null;
  return (
    <Alert type="error" showIcon style={{ marginBottom: 16 }}
      message={errors.length === 1 ? errors[0] : <ul style={{ margin: 0, paddingLeft: 18 }}>{errors.map((e) => <li key={e}>{e}</li>)}</ul>} />
  );
}

/**
 * Change the password with the current one. When currentPassword is given (login with an expired
 * password), it is not asked again.
 */
const ChangePasswordModal = ({ open, username, currentPassword, title, intro, onCancel, onChanged }) => {
  const [form] = Form.useForm();
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const close = () => {
    form.resetFields();
    setErrors([]);
    onCancel();
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (e) {
      return; // the form shows what is missing
    }
    setSaving(true);
    const result = await postPassword('change/', {
      username,
      old_password: currentPassword ?? values.old_password,
      new_password: values.new_password,
    });
    setSaving(false);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    form.resetFields();
    setErrors([]);
    onChanged(values.new_password);
  };

  return (
    <Modal open={open} title={title || 'Change password'} okText="Save new password" onOk={submit}
      confirmLoading={saving} onCancel={close} destroyOnClose maskClosable={false}>
      {intro && <div style={{ marginBottom: 16 }}>{intro}</div>}
      <ErrorList errors={errors} />
      <Form form={form} layout="vertical" onFinish={submit}>
        {currentPassword === undefined && (
          <Form.Item name="old_password" label="Current password"
            rules={[{ required: true, message: 'Please enter your current password' }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
        )}
        <NewPasswordFields />
        <button type="submit" hidden aria-hidden="true" />
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
